import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { InventoryItemSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { inventoryApi } from '@/services/inventory.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';
import { ItemForm } from './item-form';

/**
 * The Inventory directory: every article the church owns, at a glance.
 *
 * Same shape as People's and Groups' own directories — search, an archived
 * toggle driven by My Settings, and a low-stock filter that reuses the exact
 * threshold check the dashboard alert and the API both apply.
 */
export function ItemsListPage() {
  const { t } = useI18n();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const showArchived = getShowArchivedRecordsPreference();

  const deferredSearch = useDeferredValue(search);

  const query = useQuery({
    queryKey: ['inventory', 'items', { search: deferredSearch, lowStockOnly, showArchived }],
    queryFn: ({ signal }) =>
      inventoryApi.listItems(
        {
          search: deferredSearch || undefined,
          lowStockOnly: lowStockOnly || undefined,
          includeArchived: showArchived,
          limit: 50,
        },
        signal,
      ),
    placeholderData: (previous) => previous,
  });

  const items = query.data?.data ?? [];
  const isStale = query.isPlaceholderData;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('inventory.item.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('inventory.item.count', { count: items.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>

        <div className="page-actions">
          {can('inventory.item.create') && (
            <button
              type="button"
              className="btn"
              data-variant="primary"
              onClick={() => setIsCreating(true)}
            >
              {t('inventory.item.create')}
            </button>
          )}
        </div>
      </header>

      <div className="filters">
        <Field
          label={t('common.actions.search')}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('common.actions.search')}
          name="search"
        />
        <label className="field-checkbox">
          <input
            type="checkbox"
            checked={lowStockOnly}
            onChange={(event) => setLowStockOnly(event.target.checked)}
          />
          {t('inventory.item.lowStockOnly')}
        </label>
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && items.length === 0 && (
        <EmptyState
          title={search ? t('common.states.noResults') : t('inventory.item.empty')}
          hint={search ? undefined : t('inventory.item.create')}
        />
      )}

      {items.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('inventory.item.singular')}</th>
                <th scope="col">{t('inventory.category.singular')}</th>
                <th scope="col">{t('inventory.location.singular')}</th>
                <th scope="col">{t('inventory.item.quantityOnHand')}</th>
                <th scope="col">{t('inventory.item.condition')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <ItemRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <ItemForm
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function ItemRow({ item }: { item: InventoryItemSummaryResponse }) {
  const { t } = useI18n();

  return (
    <tr data-archived={item.archivedAt ? true : undefined}>
      <td>
        <Link to={`/inventory/${item.id}`} className="person-name">
          {item.name}
        </Link>
      </td>
      <td>{item.categoryName}</td>
      <td>{item.locationName}</td>
      <td dir="ltr">
        {item.quantityOnHand} {item.unit}
        {item.isLowStock && (
          <span className="cell-flag" title={t('inventory.item.lowStock')}>
            {' '}
            ⚠
          </span>
        )}
      </td>
      <td>
        <StatusBadge status={item.condition} label={t(`inventory.condition.${item.condition}`)} />
      </td>
    </tr>
  );
}
